import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInvoices, sendInvoice, deleteInvoice } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { StatusDot } from '@/components/ui/status-dot';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Search, X, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

/* Compact relative time for the read-receipt column — "3d", "2h", "just now". */
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

/* Below 640px the table collapses: header hidden, each cell stacked with its micro label. */
const ROW = 'block py-2 sm:table-row sm:py-0';
const CLICKABLE = 'cursor-pointer';
const CELL = 'flex h-auto items-baseline justify-between gap-4 px-0 py-1 before:text-micro before:uppercase before:text-ink-subtle before:content-[attr(data-label)] sm:table-cell sm:h-11 sm:px-3 sm:py-0 sm:before:content-none';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();

  const fetch = async (s) => {
    setLoading(true);
    try { const res = await getInvoices(s); setInvoices(res.data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetch(filter); }, [filter]);

  const filtered = invoices.filter(inv => {
    if (search) {
      const q = search.toLowerCase();
      const matchesClient = (inv.client_name || '').toLowerCase().includes(q);
      const matchesNumber = String(inv.invoice_number || '').includes(q);
      if (!matchesClient && !matchesNumber) return false;
    }
    if (dateFrom && new Date(inv.created_at) < new Date(dateFrom)) return false;
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      if (new Date(inv.created_at) > end) return false;
    }
    return true;
  });

  const hasFilters = search || dateFrom || dateTo;
  const clearFilters = () => { setSearch(''); setDateFrom(''); setDateTo(''); };

  const handleSend = async (inv) => {
    try {
      await sendInvoice(inv.id);
      toast.success('Invoice sent');
      fetch(filter);
    } catch { toast.error('Failed to send invoice'); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm('Delete this draft invoice?')) return;
    try {
      await deleteInvoice(inv.id);
      toast.success('Invoice deleted');
      fetch(filter);
    } catch { toast.error('Failed to delete invoice'); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Invoices"
        description={`${filtered.length} invoice${filtered.length !== 1 ? 's' : ''}${hasFilters ? ` of ${invoices.length}` : ''}`}
        actions={
          <Button onClick={() => navigate('/invoices/new')} data-testid="new-invoice-btn">
            <Plus strokeWidth={1.5} /> New invoice
          </Button>
        }
      />

      <div className="space-y-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Draft</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
            <TabsTrigger value="overdue">Overdue</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by client name or invoice number"
              className="pl-9"
              data-testid="search-invoices"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[168px] tabular-nums" data-testid="date-from" />
            <span className="text-small text-ink-subtle">to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[168px] tabular-nums" data-testid="date-to" />
            {hasFilters && (
              <Button variant="ghost" size="icon" onClick={clearFilters} aria-label="Clear filters" data-testid="clear-filters">
                <X strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 space-y-px border-t border-line pt-px">
          {[1, 2, 3].map(i => <div key={i} className="h-11 animate-pulse bg-wash" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          className="mt-6"
          title={hasFilters ? 'No invoices match your filters' : 'No invoices yet'}
          description={hasFilters ? 'Try a different search term or date range.' : 'Create your first invoice to start getting paid.'}
          action={hasFilters ? (
            <Button variant="outline" onClick={clearFilters}>Clear filters</Button>
          ) : (
            <Button onClick={() => navigate('/invoices/new')}>New invoice</Button>
          )}
        />
      ) : (
        <div className="mt-6">
          <Table>
            <TableHeader className="hidden sm:table-header-group">
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Opened</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-10"><span className="sr-only">Actions</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(inv => (
                <TableRow
                  key={inv.id}
                  className={`${ROW} ${CLICKABLE}`}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  onKeyDown={e => { if (e.key === 'Enter') navigate(`/invoices/${inv.id}`); }}
                  data-testid={`invoice-card-${inv.id}`}
                >
                  <TableCell data-label="Invoice" className={`${CELL} text-ink`}>#{inv.invoice_number}</TableCell>
                  <TableCell data-label="Client" className={`${CELL} text-ink-muted`}>{inv.client_name}</TableCell>
                  <TableCell data-label="Due" className={`${CELL} text-ink-muted`}>
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell data-label="Status" className={CELL}><StatusDot status={inv.status} /></TableCell>
                  <TableCell data-label="Opened" className={CELL}>
                    {inv.status === 'draft' ? (
                      <span className="text-ink-subtle">—</span>
                    ) : inv.view_count > 0 ? (
                      <span className="text-ink-muted">
                        {relativeTime(inv.last_viewed_at)}
                        {inv.view_count > 1 && (
                          <span className="text-ink-subtle tabular-nums"> · {inv.view_count}×</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-ink-subtle">Not opened</span>
                    )}
                  </TableCell>
                  <TableCell data-label="Amount" className={`${CELL} text-ink sm:text-right`}>{formatCurrency(inv.total)}</TableCell>
                  <TableCell
                    className={`${CELL} before:content-none sm:text-right`}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for invoice ${inv.invoice_number}`} data-testid={`actions-${inv.id}`}>
                          <MoreHorizontal strokeWidth={1.5} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {inv.status !== 'paid' && (
                          <DropdownMenuItem onSelect={() => handleSend(inv)} data-testid={`send-${inv.id}`}>
                            {inv.status === 'draft' ? 'Send invoice' : 'Resend invoice'}
                          </DropdownMenuItem>
                        )}
                        {inv.status === 'sent' && (
                          <DropdownMenuItem
                            onSelect={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/pay/${inv.token}`);
                              toast.success('Payment link copied');
                            }}
                            data-testid={`copy-link-${inv.id}`}
                          >
                            Copy payment link
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onSelect={() => {
                            import('@/lib/pdfExport').then(({ generateInvoicePDF }) => {
                              generateInvoicePDF(inv, {}, { name: inv.client_name });
                            });
                          }}
                          data-testid={`pdf-${inv.id}`}
                        >
                          Download PDF
                        </DropdownMenuItem>
                        {inv.status === 'draft' && (
                          <DropdownMenuItem onSelect={() => handleDelete(inv)} className="text-destructive focus:text-destructive" data-testid={`delete-${inv.id}`}>
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
